// Inline badge row. Used wherever a username is rendered.
//
// Pass the canonical badge ids; this component handles ordering,
// per-id icon, color, and tooltip. The container collapses to
// nothing when the user has no badges.

import { Tooltip, TooltipContent, TooltipTrigger, cn } from "@link/ui";

import { BADGES, sortBadges, type BadgeId } from "../lib/badges";

interface Props {
  badges: ReadonlyArray<BadgeId>;
  size?: "sm" | "md" | "lg";
  /**
   * Maximum number of badges to render inline. Defaults to 1 —
   * usernames stay readable, and the user can click into the
   * profile popover to see the full collection. Pass a larger
   * number for places where multiple badges fit (e.g. expanded
   * profile cards).
   */
  max?: number;
  /** Optional extra class on the wrapper. */
  className?: string;
}

export function BadgeRow({ badges, size = "sm", max = 1, className }: Props) {
  const sorted = sortBadges(badges).slice(0, Math.max(0, max));
  if (sorted.length === 0) return null;
  const sz =
    size === "lg" ? "h-4 w-4" : size === "md" ? "h-3.5 w-3.5" : "h-3 w-3";
  return (
    <span
      className={cn("inline-flex items-center gap-0.5", className)}
      aria-label="badges"
    >
      {sorted.map((id) => {
        const meta = BADGES[id];
        return (
          <Tooltip key={id}>
            <TooltipTrigger asChild>
              <span
                className={cn(
                  "inline-flex items-center justify-center",
                  meta.toneClass,
                )}
                style={{ color: meta.toneColor }}
                aria-label={meta.label}
              >
                {meta.icon(sz)}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p className="font-semibold">{meta.label}</p>
              <p className="text-[11px] opacity-80">{meta.tooltip}</p>
            </TooltipContent>
          </Tooltip>
        );
      })}
    </span>
  );
}
